/* Feb-2026 · Per-Novel Economics Card.
 *
 * Renders the GPT-drafts + Opus-edits P&L for a 500K-word novel, plus
 * the volume of users required to hit an admin-set monthly net target.
 * Backed by `GET /api/admin/economics/per-novel`.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, RefreshCw, DollarSign, Users } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const usd = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function Row({ label, value, strong, testid, tone }) {
  const toneClass =
    tone === 'good' ? 'text-emerald-700' :
    tone === 'bad'  ? 'text-rose-700'    :
    tone === 'muted'? 'text-muted-foreground' : '';
  return (
    <div className="flex items-center justify-between border-b border-border/50 py-1 last:border-b-0">
      <span className={`text-[11.5px] ${strong ? 'font-semibold' : ''}`}>{label}</span>
      <span className={`font-mono-stat tabular-nums text-[12px] ${strong ? 'font-semibold' : ''} ${toneClass}`}
            data-testid={testid}>
        {value}
      </span>
    </div>
  );
}

export default function PerNovelEconomicsCard() {
  const [data, setData]       = useState(null);
  const [busy, setBusy]       = useState(false);
  const [err,  setErr]        = useState(null);
  const [target, setTarget]   = useState(40000);
  const [editPct, setEditPct] = useState(60);
  const [studio, setStudio]   = useState(35.00);

  const load = useCallback(async () => {
    setBusy(true); setErr(null);
    try {
      const r = await axios.get(`${API}/admin/economics/per-novel`, {
        params: {
          target_net_monthly_usd: target,
          edit_pass_pct:          editPct,
          studio_price_usd:       studio,
        },
        withCredentials: true,
      });
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load per-novel economics.');
    } finally {
      setBusy(false);
    }
  }, [target, editPct, studio]);

  useEffect(() => { load(); }, [load]);

  const cogs = data?.cogs_breakdown;
  const rev  = data?.revenue_breakdown;
  const prof = data?.profit;
  const vol  = data?.volume_for_target;

  return (
    <section className="mt-6 rounded-md border border-border bg-card" data-testid="per-novel-economics-card">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-border p-3">
        <div>
          <h3 className="text-[13px] font-semibold">Per-Novel P&L · GPT draft + Opus edit</h3>
          <p className="text-[11px] text-muted-foreground">
            500K-word novel. GPT-5.2 drafts, Opus 4.7 rewrites for readability, pace, beats, hooks,
            character-arc resolution, continuity, and emotional payoff.
          </p>
        </div>
        <Button size="sm" variant="ghost" onClick={load} disabled={busy} data-testid="per-novel-refresh">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          Refresh
        </Button>
      </header>

      <div className="grid grid-cols-1 gap-3 p-3 md:grid-cols-3">
        <label className="text-[11px]">
          <div className="mb-1 flex items-center gap-1 text-muted-foreground">
            <DollarSign className="h-3 w-3" /> Monthly net target
          </div>
          <Input type="number" step="1000" min="0" value={target}
                 onChange={(e) => setTarget(Number(e.target.value) || 0)}
                 className="h-8" data-testid="per-novel-target-input" />
        </label>
        <label className="text-[11px]">
          <div className="mb-1 text-muted-foreground">Opus edit-pass volume %</div>
          <Input type="number" min="10" max="100" step="5" value={editPct}
                 onChange={(e) => setEditPct(Number(e.target.value) || 0)}
                 className="h-8" data-testid="per-novel-editpct-input" />
        </label>
        <label className="text-[11px]">
          <div className="mb-1 text-muted-foreground">Studio price share $</div>
          <Input type="number" min="0" step="1" value={studio}
                 onChange={(e) => setStudio(Number(e.target.value) || 0)}
                 className="h-8" data-testid="per-novel-studio-input" />
        </label>
      </div>

      {err && <div className="px-3 pb-3 text-[11.5px] text-rose-600" data-testid="per-novel-err">{err}</div>}

      {data && (
        <div className="grid grid-cols-1 gap-4 border-t border-border p-3 md:grid-cols-3">
          <div>
            <div className="mb-1.5 text-[10.5px] uppercase tracking-wider text-muted-foreground">Cost of Goods Sold</div>
            <Row label="GPT-5.2 draft"      value={usd(cogs?.gpt_draft_usd)}  tone="muted" testid="per-novel-cogs-gpt" />
            <Row label="Opus 4.7 rewrite"   value={usd(cogs?.opus_edit_usd)}  tone="muted" testid="per-novel-cogs-opus" />
            <Row label="Adjacent (cache/retry)" value={usd(cogs?.adjacent_usd)}   tone="muted" testid="per-novel-cogs-adj" />
            <Row label="Ancillary (scan/storage)" value={usd(cogs?.ancillary_usd)}  tone="muted" testid="per-novel-cogs-anc" />
            <Row label="Total COGS"          value={usd(cogs?.total_cogs_usd)} strong tone="bad" testid="per-novel-cogs-total" />
          </div>
          <div>
            <div className="mb-1.5 text-[10.5px] uppercase tracking-wider text-muted-foreground">Revenue</div>
            <Row label="Studio share"          value={usd(rev?.studio_share_usd)}   tone="muted" testid="per-novel-rev-studio" />
            <Row label="PAYG Opus credits"     value={usd(rev?.payg_opus_usd)}      tone="muted" testid="per-novel-rev-payg" />
            <Row label="Launch Kit attach"     value={usd(rev?.launch_kit_usd)}     tone="muted" testid="per-novel-rev-launch" />
            <Row label="Cover Pack attach"     value={usd(rev?.cover_pack_usd)}     tone="muted" testid="per-novel-rev-cover" />
            <Row label="Gross revenue"         value={usd(rev?.gross_revenue_usd)}  strong tone="good" testid="per-novel-rev-total" />
          </div>
          <div>
            <div className="mb-1.5 text-[10.5px] uppercase tracking-wider text-muted-foreground">Profit</div>
            <Row label="Gross profit"          value={usd(prof?.gross_profit_usd)}                 testid="per-novel-prof-gross" />
            <Row label="Gross margin"          value={`${prof?.gross_margin_pct ?? 0}%`}    tone="muted" testid="per-novel-prof-gross-pct" />
            <Row label="Stripe fee"            value={usd(prof?.stripe_fee_usd)}          tone="muted" testid="per-novel-prof-stripe" />
            <Row label="Infra allocation"      value={usd(prof?.infra_usd)}               tone="muted" testid="per-novel-prof-infra" />
            <Row label="Net profit / novel"    value={usd(prof?.net_profit_usd)}    strong tone={prof?.net_profit_usd >= 0 ? 'good' : 'bad'} testid="per-novel-prof-net" />
            <Row label="Net margin"            value={`${prof?.net_margin_pct ?? 0}%`}      strong tone={prof?.net_margin_pct >= 20 ? 'good' : 'bad'} testid="per-novel-prof-net-pct" />
          </div>
        </div>
      )}

      {vol && (
        <div className="grid grid-cols-1 gap-3 border-t border-border p-3 md:grid-cols-2">
          <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3">
            <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-wider text-emerald-700">
              <Users className="h-3 w-3" /> Users to hit target (1 novel / user / mo)
            </div>
            <div className="mt-1 font-mono-stat text-[22px] font-semibold text-emerald-700" data-testid="per-novel-users-cadence">
              {vol.users_at_novel_cadence?.toLocaleString() ?? '—'}
            </div>
            <div className="text-[11px] text-muted-foreground">
              Power-authors publishing a full 500K-word novel every month.
            </div>
          </div>
          <div className="rounded-md border border-primary/30 bg-primary/5 p-3">
            <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-wider text-primary">
              <Users className="h-3 w-3" /> Users at blended ARPU (${vol.net_per_user_monthly.toFixed(2)} net)
            </div>
            <div className="mt-1 font-mono-stat text-[22px] font-semibold text-primary" data-testid="per-novel-users-blended">
              {vol.users_at_blended_net?.toLocaleString() ?? '—'}
            </div>
            <div className="text-[11px] text-muted-foreground">
              Realistic mix — 60% Studio (0.5 novels / mo) + 20% Pro + 15% Starter + 5% BYOK.
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
